/**
 * Gemelo_Digital (digital twin) de la Capa Operacional AI-First (R15).
 *
 * Implementa la interfaz {@link Twin} de `../api/contracts` para la
 * variante **demo** (client-side, R17.1/R17.5). El Gemelo_Digital ofrece
 * tres capacidades, todas **deterministas** y **sin tocar el estado real
 * de la red** (no registra Eventos_Operacionales de mutación, R15.1,
 * R15.4):
 *
 *   - **Simulacion_Hidraulica** ({@link TwinLocal.simulate}): estima la
 *     presión y el flujo aguas abajo de un escenario propuesto
 *     **reutilizando la propagación downstream** de `./isolation`
 *     (`simulate(grafo, escenario)`), de modo que el modelo hidráulico
 *     viva en un único lugar. El resultado se etiqueta `es_simulacion:
 *     true` (R15.1, R15.4, R15.5).
 *
 *   - **Replay temporal** ({@link TwinLocal.replay}): reconstruye de
 *     forma determinista los estados pasados de las entidades **desde el
 *     Event_Store**, acotados a `[rango.inicio, rango.fin]`, reutilizando
 *     `replayEventos` de `./eventStore`. Exige `inicio < fin` y aborta
 *     ante datos históricos insuficientes (R15.2, R15.6).
 *
 *   - **Forecast operacional** ({@link TwinLocal.forecast}): produce una
 *     predicción de presión/pérdidas para un horizonte configurable en
 *     `[1, 168]` horas mediante una **regresión lineal simple
 *     determinista** (media móvil/mínimos cuadrados), acompañada de un
 *     nivel de confianza `∈ [0, 1]`. Aborta ante datos históricos
 *     insuficientes (R15.3, R15.6).
 *
 * Determinismo (R15.5): dadas la misma entrada, los mismos parámetros y
 * la misma versión de código, dos cálculos sucesivos producen el mismo
 * resultado. Las funciones núcleo ({@link forecastDeterminista},
 * {@link simularDownstream}, {@link reconstruirEstados}) son **puras** y
 * se exportan para que los tests PBT (Property 40) las ejerciten sin
 * IndexedDB ni instanciar la clase.
 */

import type {
  EntidadRef,
  EscenarioSimulacion,
  EstadoEntidad,
  EventStore,
  EventoOperacional,
  Forecast,
  IsoTimestamp,
  RangoTemporal,
  ResultadoSimulado,
  Result,
  Twin,
} from "../api/contracts";
import { provideDemo, runtime } from "../api/runtime";
import { replayEventos } from "./eventStore";
import { type ProyeccionGrafo } from "./graph";
import { simulate as simulateDownstream } from "./isolation";

// ---------------------------------------------------------------------------
// Parámetros del modelo de forecast (demo, determinista)
// ---------------------------------------------------------------------------

/** Horizonte mínimo de forecast en horas (R15.3). */
export const HORIZONTE_MIN_H = 1;

/** Horizonte máximo de forecast en horas (R15.3). */
export const HORIZONTE_MAX_H = 168;

/**
 * Número mínimo de puntos históricos para producir un forecast. Por
 * debajo de este umbral los datos son insuficientes y el cálculo aborta
 * (R15.6).
 */
export const FORECAST_MIN_HISTORIA = 3;

/** Intervalo por defecto entre puntos cuando no puede inferirse, en ms (1 hora). */
const PASO_MS_DEFECTO = 3_600_000;

/** Marca persistente de resultado del Gemelo_Digital (R15.4). */
export const MARCA_SIMULACION = "simulación";

// ---------------------------------------------------------------------------
// Tipos auxiliares
// ---------------------------------------------------------------------------

/** Punto de una serie temporal histórica que alimenta el forecast. */
export interface PuntoSerie {
  ts: IsoTimestamp;
  valor: number;
}

// ---------------------------------------------------------------------------
// Helpers numéricos deterministas
// ---------------------------------------------------------------------------

/** Acota un valor al rango [0, 1]. */
function clamp01(x: number): number {
  if (!Number.isFinite(x)) return 0;
  if (x < 0) return 0;
  if (x > 1) return 1;
  return x;
}

/** ¿Es `h` un horizonte válido (entero en `[1, 168]`)? (R15.3). */
export function horizonteValido(h: number): boolean {
  return Number.isInteger(h) && h >= HORIZONTE_MIN_H && h <= HORIZONTE_MAX_H;
}

/**
 * Infiere el paso temporal (en ms) entre puntos históricos como el
 * promedio determinista de los deltas consecutivos. Cae a
 * {@link PASO_MS_DEFECTO} si no puede inferirse (timestamps no
 * parseables o serie de un solo punto).
 */
function inferirPasoMs(historia: PuntoSerie[]): number {
  let suma = 0;
  let cuenta = 0;
  for (let i = 1; i < historia.length; i++) {
    const t0 = Date.parse(historia[i - 1].ts);
    const t1 = Date.parse(historia[i].ts);
    if (Number.isFinite(t0) && Number.isFinite(t1) && t1 > t0) {
      suma += t1 - t0;
      cuenta += 1;
    }
  }
  if (cuenta === 0) return PASO_MS_DEFECTO;
  return Math.round(suma / cuenta);
}

// ---------------------------------------------------------------------------
// Forecast determinista (regresión lineal simple por mínimos cuadrados)
// ---------------------------------------------------------------------------

/**
 * Produce un forecast operacional determinista para un horizonte de
 * `horizonte_h` horas a partir de una serie histórica (R15.3, R15.6).
 *
 *   1. Valida que `horizonte_h` sea un entero en `[1, 168]`; en otro caso
 *      devuelve un `Result` de error (rango inválido).
 *   2. Si la historia tiene menos de {@link FORECAST_MIN_HISTORIA} puntos
 *      → **aborta**: datos históricos insuficientes (R15.6).
 *   3. Ajusta una recta `y = m·x + b` por mínimos cuadrados sobre los
 *      índices `x = 0..n-1` y predice `horizonte_h` puntos futuros con
 *      timestamps espaciados según el paso inferido de la historia.
 *   4. Calcula la confianza a partir del coeficiente de determinación R²
 *      del ajuste, atenuado por la longitud del horizonte, acotado a
 *      `[0, 1]`.
 *
 * Función **pura y determinista**: misma serie y mismo horizonte → mismo
 * resultado (R15.5).
 */
export function forecastDeterminista(historia: PuntoSerie[], horizonte_h: number): Result<Forecast> {
  if (!horizonteValido(horizonte_h)) {
    return {
      ok: false,
      error: `El horizonte de forecast debe ser un entero en [${HORIZONTE_MIN_H}, ${HORIZONTE_MAX_H}] horas (recibido: ${horizonte_h}).`,
    };
  }

  if (!historia || historia.length < FORECAST_MIN_HISTORIA) {
    return {
      ok: false,
      error: `Datos históricos insuficientes para el forecast: se requieren al menos ${FORECAST_MIN_HISTORIA} puntos (disponibles: ${historia?.length ?? 0}).`,
    };
  }

  const n = historia.length;
  const ys = historia.map((p) => p.valor);
  if (ys.some((y) => !Number.isFinite(y))) {
    return { ok: false, error: "Datos históricos insuficientes para el forecast: contienen valores no numéricos." };
  }

  // Mínimos cuadrados sobre x = 0..n-1.
  const sumX = (n * (n - 1)) / 2;
  const sumXX = ((n - 1) * n * (2 * n - 1)) / 6;
  let sumY = 0;
  let sumXY = 0;
  for (let i = 0; i < n; i++) {
    sumY += ys[i];
    sumXY += i * ys[i];
  }
  const denom = n * sumXX - sumX * sumX;
  const mediaY = sumY / n;
  // Pendiente e intercepto; serie degenerada (denom = 0) → recta constante.
  const m = denom !== 0 ? (n * sumXY - sumX * sumY) / denom : 0;
  const b = denom !== 0 ? (sumY - m * sumX) / n : mediaY;

  // Coeficiente de determinación R² del ajuste sobre la historia.
  let ssTot = 0;
  let ssRes = 0;
  for (let i = 0; i < n; i++) {
    const yhat = m * i + b;
    ssTot += (ys[i] - mediaY) ** 2;
    ssRes += (ys[i] - yhat) ** 2;
  }
  // Serie plana (ssTot = 0): ajuste perfecto si no hay residuo.
  const r2 = ssTot === 0 ? (ssRes === 0 ? 1 : 0) : 1 - ssRes / ssTot;

  // Atenuación determinista de la confianza por longitud de horizonte:
  // a mayor horizonte, menor confianza, conservando el rango [0, 1].
  const factorHorizonte = 1 - 0.5 * ((horizonte_h - 1) / (HORIZONTE_MAX_H - 1));
  const confianza = clamp01(clamp01(r2) * factorHorizonte);

  // Predicción: horizonte_h puntos a partir del último instante histórico.
  const pasoMs = inferirPasoMs(historia);
  const ultimoTs = Date.parse(historia[n - 1].ts);
  const baseMs = Number.isFinite(ultimoTs) ? ultimoTs : 0;

  const pred = Array.from({ length: horizonte_h }, (_, h) => {
    const x = n - 1 + (h + 1);
    const valor = m * x + b;
    const ts = new Date(baseMs + (h + 1) * pasoMs).toISOString();
    return { ts, valor };
  });

  return { ok: true, value: { pred, confianza } };
}

// ---------------------------------------------------------------------------
// Simulacion_Hidraulica — reutiliza la propagación downstream de isolation
// ---------------------------------------------------------------------------

/**
 * Simulacion_Hidraulica determinista sobre un escenario propuesto,
 * **reutilizando** la propagación downstream de `./isolation` (R2.3,
 * R15.1, R15.4, R15.5). Función pura: recibe el grafo de red, no muta el
 * estado real ni registra eventos. El resultado siempre queda etiquetado
 * `es_simulacion: true` (R2.5, R15.4).
 */
export function simularDownstream(
  grafo: ProyeccionGrafo,
  escenario: EscenarioSimulacion,
): Result<ResultadoSimulado> {
  return simulateDownstream(grafo, escenario);
}

// ---------------------------------------------------------------------------
// Replay temporal — reconstrucción determinista acotada al rango
// ---------------------------------------------------------------------------

/** Clave estable de una entidad para agrupar/ordenar de forma determinista. */
function entityKey(ref: EntidadRef): string {
  return `${ref.tipo}:${ref.id}`;
}

/**
 * Reconstruye de forma **determinista** los estados de todas las
 * entidades a partir de un log de Eventos_Operacionales, acotando la
 * reproducción a `[rango.inicio, rango.fin]` (R15.2). Reutiliza
 * `replayEventos` de `./eventStore` por entidad, alimentándolo solo con
 * los eventos dentro del rango para mantener la cota inferior.
 *
 *   - Exige `rango.inicio` estrictamente anterior a `rango.fin`.
 *   - Si no hay eventos en el rango → datos históricos insuficientes,
 *     aborta (R15.6).
 *   - Cada estado se etiqueta con la marca persistente "simulación"
 *     (`estado.__simulacion`), separándolo del estado real (R15.4).
 *
 * Función pura y determinista: el resultado solo depende del log y del
 * rango (no del orden de inserción), y los estados se devuelven ordenados
 * por entidad (R15.5).
 */
export function reconstruirEstados(
  eventos: EventoOperacional[],
  rango: RangoTemporal,
): Result<EstadoEntidad[]> {
  const inicio = Date.parse(rango.inicio);
  const fin = Date.parse(rango.fin);

  // inicio estrictamente anterior a fin (R15.2).
  const inicioMenorQueFin = Number.isFinite(inicio) && Number.isFinite(fin)
    ? inicio < fin
    : rango.inicio < rango.fin;
  if (!inicioMenorQueFin) {
    return {
      ok: false,
      error: "El rango de replay es inválido: el instante inicial debe ser estrictamente anterior al final (R15.2).",
    };
  }

  // Eventos dentro del rango [inicio, fin] (cota inferior y superior).
  const enRango = eventos.filter((e) => {
    const t = Date.parse(e.ts);
    if (Number.isFinite(t) && Number.isFinite(inicio) && Number.isFinite(fin)) {
      return t >= inicio && t <= fin;
    }
    return e.ts >= rango.inicio && e.ts <= rango.fin;
  });

  if (enRango.length === 0) {
    return {
      ok: false,
      error: "Datos históricos insuficientes para el replay: no hay Eventos_Operacionales en el rango solicitado (R15.6).",
    };
  }

  // Entidades distintas presentes en el rango, en orden determinista.
  const refs = new Map<string, EntidadRef>();
  for (const e of enRango) refs.set(entityKey(e.entidad_ref), e.entidad_ref);

  const estados = [...refs.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([, ref]) => {
      const estado = replayEventos(enRango, ref, rango.fin);
      // Marca persistente "simulación": separa el resultado del estado real (R15.4).
      return {
        ...estado,
        estado: { ...estado.estado, __simulacion: MARCA_SIMULACION },
      } satisfies EstadoEntidad;
    });

  return { ok: true, value: estados };
}

// ---------------------------------------------------------------------------
// TwinLocal — implementación demo de la interfaz Twin (R15)
// ---------------------------------------------------------------------------

/** Opciones de construcción del {@link TwinLocal}. */
export interface TwinLocalOptions {
  /**
   * Event_Store del que se reconstruyen los estados pasados (R15.2). Si
   * se omite, `replay` reporta datos insuficientes.
   */
  eventStore?: EventStore;
  /**
   * Grafo de red por defecto para la Simulacion_Hidraulica cuando el
   * argumento `modelo` de {@link TwinLocal.simulate} no lo aporta.
   */
  grafo?: ProyeccionGrafo;
  /**
   * Serie histórica que alimenta el forecast (presión/pérdidas). Puede
   * ser un proveedor para reflejar datos cargados de forma diferida.
   */
  historia?: PuntoSerie[] | (() => PuntoSerie[]);
}

/** ¿Es `modelo` una {@link ProyeccionGrafo} utilizable? */
function esProyeccionGrafo(modelo: unknown): modelo is ProyeccionGrafo {
  return (
    typeof modelo === "object" &&
    modelo !== null &&
    Array.isArray((modelo as ProyeccionGrafo).nodos) &&
    Array.isArray((modelo as ProyeccionGrafo).aristas)
  );
}

/**
 * Gemelo_Digital client-side que implementa {@link Twin}. Reutiliza la
 * propagación downstream de `./isolation` (simulación) y el replay
 * determinista del Event_Store (`./eventStore`), y añade un forecast
 * determinista por regresión lineal simple. No escribe nunca en el
 * Event_Store: todos los cálculos son lecturas/derivaciones puras que
 * preservan intacto el estado real (R15.1, R15.4).
 */
export class TwinLocal implements Twin {
  private readonly eventStore?: EventStore;
  private readonly grafo?: ProyeccionGrafo;
  private readonly historiaProvider?: PuntoSerie[] | (() => PuntoSerie[]);

  constructor(opts?: TwinLocalOptions) {
    this.eventStore = opts?.eventStore;
    this.grafo = opts?.grafo;
    this.historiaProvider = opts?.historia;
  }

  /** Serie histórica resuelta (acepta proveedor perezoso). */
  private historia(): PuntoSerie[] {
    const h = typeof this.historiaProvider === "function" ? this.historiaProvider() : this.historiaProvider;
    return h ?? [];
  }

  /**
   * Simulacion_Hidraulica determinista y etiquetada "simulación" (R15.1,
   * R15.4, R15.5). `modelo` puede aportar el grafo de red; en su defecto
   * se usa el grafo inyectado. `params` y `code_version` forman parte del
   * contrato de determinismo pero no alteran el resultado de este modelo
   * demo (función pura).
   */
  async simulate(
    modelo: unknown,
    escenario: EscenarioSimulacion,
    _params: Record<string, unknown>,
    _code_version: string,
  ): Promise<Result<ResultadoSimulado>> {
    const grafo = esProyeccionGrafo(modelo) ? modelo : this.grafo;
    if (!grafo) {
      return { ok: false, error: "La simulación no pudo completarse: no hay modelo de red disponible (R2.8)." };
    }
    return simularDownstream(grafo, escenario);
  }

  /**
   * Replay temporal determinista desde el Event_Store, acotado al rango
   * (R15.2). Requiere `inicio < fin` y aborta ante datos insuficientes
   * (R15.6).
   */
  async replay(rango: RangoTemporal): Promise<Result<EstadoEntidad[]>> {
    if (!this.eventStore) {
      return {
        ok: false,
        error: "Datos históricos insuficientes para el replay: no hay Event_Store disponible (R15.6).",
      };
    }

    // Validación temprana del rango antes de leer el log (R15.2).
    const inicio = Date.parse(rango.inicio);
    const fin = Date.parse(rango.fin);
    const valido = Number.isFinite(inicio) && Number.isFinite(fin) ? inicio < fin : rango.inicio < rango.fin;
    if (!valido) {
      return {
        ok: false,
        error: "El rango de replay es inválido: el instante inicial debe ser estrictamente anterior al final (R15.2).",
      };
    }

    const eventos: EventoOperacional[] = [];
    try {
      for await (const e of this.eventStore.streamFrom(1)) eventos.push(e);
    } catch (err) {
      const detalle = err instanceof Error ? err.message : String(err);
      return { ok: false, error: `No se pudo leer el Event_Store para el replay: ${detalle}`, degraded: true };
    }

    return reconstruirEstados(eventos, rango);
  }

  /**
   * Forecast operacional determinista con horizonte `∈ [1, 168]` y
   * confianza `∈ [0, 1]` (R15.3). Aborta ante datos históricos
   * insuficientes (R15.6).
   */
  async forecast(horizonte_h: number): Promise<Result<Forecast>> {
    return forecastDeterminista(this.historia(), horizonte_h);
  }
}

/** Crea una instancia DEMO del Gemelo_Digital (R15). */
export function crearTwinLocal(opts?: TwinLocalOptions): TwinLocal {
  return new TwinLocal(opts);
}

// ---------------------------------------------------------------------------
// Registro del proveedor DEMO en el runtime selector (R17.2)
// ---------------------------------------------------------------------------

/** Versión de código para el determinismo del Gemelo_Digital (R15.5). */
export const TWIN_CODE_VERSION = "ai-operational-layer@8.1";

let proyeccionGrafo: ProyeccionGrafo | undefined;
let historiaForecast: PuntoSerie[] | undefined;
let registrado = false;

/**
 * Registra (de forma idempotente) la implementación DEMO del
 * Gemelo_Digital en el runtime selector, de modo que las vistas lo
 * consuman SIEMPRE vía `runtime.twin()` (R17.1, R17.2, R17.5) y nunca
 * instancien el motor directamente. El Event_Store se comparte con el
 * resto de la Capa a través del propio runtime selector para que el
 * replay use la misma cadena de eventos (R15.2, R9).
 *
 * Acepta, de forma opcional, el grafo de red proyectado (para la
 * Simulacion_Hidraulica) y la serie histórica (para el forecast); ambos
 * pueden suministrarse más tarde reinvocando esta función con los datos
 * cargados de forma diferida. El wiring final de la vista corresponde a
 * un bloque posterior.
 */
export function registerTwinRuntime(opts?: { grafo?: ProyeccionGrafo; historia?: PuntoSerie[] }): void {
  if (opts?.grafo) proyeccionGrafo = opts.grafo;
  if (opts?.historia) historiaForecast = opts.historia;
  if (registrado && !opts?.grafo && !opts?.historia) return;

  provideDemo(
    "twin",
    () =>
      new TwinLocal({
        eventStore: runtime.eventStore(),
        grafo: proyeccionGrafo,
        historia: () => historiaForecast ?? [],
      }),
  );
  registrado = true;
}
